/**
 * OGP Preview Handler
 *
 * Coordinates debounced OGP preview fetching and rendering for text inputs.
 */

import { OgpRender, renderOgpLimitWarning } from "./ogp-render";
import { translator } from "../../translation/translator";
import { ogpPreviewForm } from "./ogp-preview-form";
import { OgpPreviewListener } from "./ogp-preview-listener";

type PreviewInputElement = HTMLTextAreaElement | HTMLInputElement;
type PreviewContainerElement = HTMLElement;

class OgpPreviewHandler {
    public ogpRender: OgpRender;

    private debounceTimer: ReturnType<typeof setTimeout> | null;

    private lastProcessedUrls: Set<string>;

    public constructor() {
        this.ogpRender = new OgpRender();
        this.debounceTimer = null;
        this.lastProcessedUrls = new Set();
    }

    public init(textareaElement: PreviewInputElement, previewContainerElement: PreviewContainerElement): void {
        if (!textareaElement || !previewContainerElement) {
            console.warn("OgpPreviewHandler: Missing required elements");
            return;
        }

        OgpPreviewListener.bindPreviewInput(textareaElement, (event) => {
            const element = ogpPreviewForm.getEventPreviewInput(event);
            if (!element) {
                return;
            }
            this.handleInputChange(element.value, previewContainerElement);
        });

        if (textareaElement.value) {
            void this.updatePreview(textareaElement.value, previewContainerElement);
        }
    }

    public handleInputChange(content: string, previewContainer: PreviewContainerElement): void {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
            void this.updatePreview(content, previewContainer);
        }, 1000);
    }

    public async updatePreview(content: string, previewContainer: PreviewContainerElement): Promise<void> {
        const urls = this.ogpRender.detectUrls(content);
        const renderMode = previewContainer?.classList?.contains("ogp-preview-container--compact") ? "compact" : "full";

        const currentUrls = new Set(urls);
        for (const url of this.lastProcessedUrls) {
            if (!currentUrls.has(url)) {
                this.lastProcessedUrls.delete(url);
            }
        }

        const newUrls = urls.filter((url) => !this.lastProcessedUrls.has(url));
        if (newUrls.length === 0 && urls.length === this.lastProcessedUrls.size) {
            return;
        }

        if (urls.length !== this.lastProcessedUrls.size) {
            previewContainer.innerHTML = "";
            this.lastProcessedUrls.clear();
        }

        if (urls.length === 0) {
            this.setPreviewSectionVisibility(previewContainer, false);
            return;
        }

        const maxPreviews = 1;
        const urlsToProcess = urls.slice(0, maxPreviews);

        for (const url of urlsToProcess) {
            if (!this.lastProcessedUrls.has(url)) {
                await this.ogpRender.fetchAndRender(url, previewContainer, renderMode);
                if (this.hasRenderedPreview(previewContainer)) {
                    this.lastProcessedUrls.add(url);
                }
            }
        }

        const hasPreview = this.hasRenderedPreview(previewContainer);
        if (!hasPreview) {
            previewContainer.innerHTML = "";
            this.lastProcessedUrls.clear();
            this.setPreviewSectionVisibility(previewContainer, false);
            return;
        }

        this.setPreviewSectionVisibility(previewContainer, true);

        if (urls.length > maxPreviews) {
            this.showLimitWarning(previewContainer, urls.length, maxPreviews);
        }
    }

    public async updatePreviewWithUrls(urls: string[], container: PreviewContainerElement): Promise<void> {
        if (!container) return;

        const renderMode = container?.classList?.contains("ogp-preview-container--compact") ? "compact" : "full";
        const eligibleUrls = this.ogpRender.filterEligibleUrls(urls);
        const maxPreviews = 1;
        const urlsToProcess = eligibleUrls.slice(0, maxPreviews);

        if (
            urlsToProcess.length === this.lastProcessedUrls.size &&
            urlsToProcess.every((url) => this.lastProcessedUrls.has(url))
        ) {
            return;
        }

        container.innerHTML = "";
        this.lastProcessedUrls.clear();

        for (const url of urlsToProcess) {
            await this.ogpRender.fetchAndRender(url, container, renderMode);
            if (this.hasRenderedPreview(container)) {
                this.lastProcessedUrls.add(url);
            }
        }

        const hasPreview = this.hasRenderedPreview(container);
        if (!hasPreview) {
            container.innerHTML = "";
            this.lastProcessedUrls.clear();
            this.setPreviewSectionVisibility(container, false);
            return;
        }

        this.setPreviewSectionVisibility(container, true);

        if (eligibleUrls.length > maxPreviews) {
            this.showLimitWarning(container, eligibleUrls.length, maxPreviews);
        }
    }

    public hasRenderedPreview(container: PreviewContainerElement): boolean {
        if (!container) return false;
        return !!ogpPreviewForm.getRenderedPreview(container);
    }

    public setPreviewSectionVisibility(container: PreviewContainerElement, visible: boolean): void {
        const section = ogpPreviewForm.getPreviewSection(container);
        if (section instanceof HTMLElement) {
            section.style.display = visible ? "block" : "none";
        }

        const root = ogpPreviewForm.getRootForm(container) || ogpPreviewForm.getPostCreateForm(container) || null;
        const contentImagesManager =
            (root instanceof HTMLElement && ogpPreviewForm.getContentImagesManager(root)) ||
            ogpPreviewForm.getContentImagesManager(document);

        if (contentImagesManager instanceof HTMLElement) {
            contentImagesManager.style.display = visible ? "" : "none";
        }
    }

    public showLimitWarning(container: PreviewContainerElement, totalUrls: number, maxUrls: number): void {
        const message =
            translator?.getTranslation("ogp_preview_limit_warning", { totalUrls, maxUrls }) ||
            `${totalUrls}個のURLが検出されましたが、プレビューは最初の${maxUrls}件のみ表示されます。`;

        renderOgpLimitWarning(container, {
            totalUrls,
            maxUrls,
            message,
        });
    }

    public clear(previewContainer?: PreviewContainerElement): void {
        if (previewContainer) {
            previewContainer.innerHTML = "";
        }
        this.lastProcessedUrls.clear();
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }

    public destroy(): void {
        this.clear();
        this.ogpRender.cache.clear();
    }
}

export { OgpPreviewHandler };
